import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { ENDPOINTS } from '../../services/apiConfig';
import { loginUser, isAuthenticated } from "../../AuthHandlers"; // Import authentication utilities
import "./Login.css";

const Login = () => {  
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  
  const RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Replace with your actual key

  // Check if user is already authenticated when component mounts
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/d/vendor-dashboard", { replace: true });
    }
    
    // Listen for storage events from other tabs
    const handleStorageChange = (event) => {
      // If token was added in another tab, redirect to dashboard
      if (event.key === 'token' && event.newValue) {
        navigate("/d/vendor-dashboard", { replace: true });
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  const validateFields = () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address.", {
        position: "top-center"
      });
      return false;
    }
    if (!password) {
      toast.error("Password is required.", {
        position: "top-center"
      });
      return false;
    }
    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA verification.", {
        position: "top-center"
      });
      return false;
    }
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;
  
    setLoading(true);
    try {
      // Use direct axios call instead of the wrapper to diagnose the issue
      const apiClient = axios.create({
        baseURL: 'http://203.161.55.192:8086',
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const response = await apiClient.post(ENDPOINTS.vendor.login, {
        email,
        password,
        recaptchaToken: captchaToken,
      });
      
      // Get data directly from the axios response
      const data = response.data;
      
      if (data && data.jwtToken) {
        // Use the loginUser utility function from AuthHandlers
        loginUser(
          data.jwtToken, 
          data.expiresIn / 60, // Convert seconds to minutes
          data.userInfo || {} // Include user info if available
        );
  
        toast.success(data.message || "Login successful! Redirecting...", {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          onClose: () => navigate("/d/vendor-dashboard", { replace: true }),
        });
      } else {
        toast.warn(data?.message || "Unexpected response from server.", {
          position: 'top-center',
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.log("Login error:", error);
      
      const errorData = error.response?.data;
      
      if (errorData) {
        // Handle specific error messages based on status code and message
        if (errorData.status === 403 && errorData.message === "Vendor not found") {
          toast.error("Account not found. Please check your email or sign up.", {
            position: 'top-center',
            autoClose: 3000,
          });
        } else if (errorData.status === 403 && errorData.message && typeof errorData.message === 'string' && errorData.message.includes("Account is locked")) {
          toast.error("Account is locked due to too many failed login attempts. Try again later.", {
            position: 'top-center',
            autoClose: 3000,
          });
        } else {
          // Handle other status codes
          switch (errorData.status) {
            case 400:
              toast.error("Invalid email or password format", {
                position: 'top-center'
              });
              break;
            case 401:
              toast.error("Invalid credentials. Please check your email and password.", {
                position: 'top-center'
              });
              break;
            case 403:
              toast.error(errorData.message || "Access denied", {
                position: 'top-center'
              });
              break;
            case 429:
              toast.error("Too many attempts. Please try again later.", {
                position: 'top-center'
              });
              break;
            case 500:
              toast.error("Server error. Please try again later.", {
                position: 'top-center'
              });
              break;
            default:
              toast.error(errorData.message || "Login failed", {
                position: 'top-center'
              });
          }
        }
      } else if (error.request) {
        // The request was made but no response was received
        toast.error("Failed to connect to the server. Please check your internet connection and try again.", {
          position: 'top-center',
          autoClose: 3000,
        });
      } else {
        // Something happened in setting up the request
        toast.error("An unexpected error occurred.", {
          position: 'top-center',
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = () => navigate("/register");
  const handleForgotPassword = () => navigate("/forgot-password");

  return (
    <div className="login-container">
      <ToastContainer />
      <div className="login-card">
        <div className="login-logo-container">
          <img src="/assets/genie.png" alt="Genie Logo" className="login-logo" />
        </div>

        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Please sign in to your account</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <div className="login-form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="youremail@example.com"
              required
            />
          </div>

          <div className="login-form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="recaptcha-container">
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input type="checkbox" />
              <span>Remember me</span>
            </label>
            <button 
              type="button" 
              className="forgot-password" 
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" className="submit-button" disabled={loading || !captchaToken}>
            {loading ? "Signing in..." : "Sign in"}
          </button>

          <p className="signup-prompt">
            Don't have an account? <button type="button" onClick={handleSignup}>Sign up</button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;